import { useUploadContext } from '@components/Files/UploadProvider';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import {
  BillingAccountService,
  type GetManyForUserResponseDto,
  JobsService,
  LayoutsService,
  MediaService,
  PaymentService,
  PayService,
} from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { v4 } from 'uuid';

export enum BulkJobStep {
  SelectFiles = 'SelectFiles',
  UploadFiles = 'UploadFiles',
  AnalyzingFiles = 'AnalyzingFiles',
  CreateJobs = 'CreateJobs',
  SubmitPrice = 'SubmitPrice',
  JobsSubmittedSuccessfully = 'JobsSubmittedSuccessfully',
  JobsSubmittedWithErrors = 'JobsSubmittedWithErrors',
}

type TCreateJob = {
  id: string;
  layoutId?: string | null;
  name: string;
};

type TState = {
  step: BulkJobStep;
  setStep: (v: BulkJobStep) => void;
  isLoading: boolean;
  billingChecksInProgress: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  error: string | null;
  layouts: GetManyForUserResponseDto[];
  createJobs: (jobs: TCreateJob[]) => void;
  uploadFiles: () => void;
  jobs: TCreateJob[];
  totalDuration: number;
  submitJobs: () => void;
  isFormDisabled: boolean;
  balanceInCents: number;
  tierRate: number;
  refreshBalance: () => Promise<void>;
};

const ModalContext = createContext<TState>({
  step: BulkJobStep.SelectFiles,
  setStep: () => {},
  isLoading: false,
  billingChecksInProgress: true,
  onCancel: () => {},
  onSuccess: () => {},
  error: null,
  layouts: [],
  createJobs: () => {},
  uploadFiles: () => {},
  jobs: [],
  totalDuration: 0,
  submitJobs: () => {},
  isFormDisabled: false,
  balanceInCents: 0,
  tierRate: 0,
  refreshBalance: async () => {},
});

export type TModalProviderProps = {
  children: ReactNode;
  onCancel: () => void;
  onSuccess: () => void;
};

export const ModalProvider = ({ children, onCancel, onSuccess }: TModalProviderProps) => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const { workspace, organization } = useAuthContext();
  const { files, startAll, setExtra } = useUploadContext();
  const [step, setStep] = useState(BulkJobStep.SelectFiles);
  const [isLoading, setIsLoading] = useState(false);
  const [billingChecksInProgress, setBillingChecksInProgress] = useState(true);
  const [isFormDisabled, setFormDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [layouts, setLayouts] = useState<GetManyForUserResponseDto[]>([]);
  const [jobs, setJobs] = useState<TCreateJob[]>([]);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [balanceInCents, setBalanceInCents] = useState<number>(0);
  const [tierRate, setTierRate] = useState<number>(0);

  const refreshBalance = async () => {
    const baId = organization?.billingAccount?.id;
    if (!baId) {
      return;
    }
    const billingAccount = await call(BillingAccountService.findOne({ baId }));
    if (billingAccount) {
      setBalanceInCents(billingAccount.currentBalance ?? 0);
      setTierRate(Number(billingAccount.tierRate) || 0);
    }
  };

  useEffect(() => {
    call(PaymentService.billingAccountHasPaymentMethod({ baId: String(organization?.billingAccount?.id) })).then(
      async (response) => {
        if (!response.result) {
          if (!organization?.permissions.viewBilling) {
            openModal(ModalNamesEnum.GenericErrorModal, {
              message: 'Your organization has no payment methods. Please contact administrator.',
            });
            return;
          }
          openModal(ModalNamesEnum.AddPaymentMethod, {
            onCancel,
            onSuccess: () => {
              openModal(ModalNamesEnum.BulkJobCreation, {
                onSuccess,
              });
            },
          });
        }
        await refreshBalance();
        setBillingChecksInProgress(false);
      },
    );
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        if (!workspace?.id || !organization?.id) {
          return;
        }
        const layouts = await call(() => LayoutsService.getOrganizationLayouts({ oid: organization.id }));
        setLayouts(layouts);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (step !== BulkJobStep.SelectFiles) {
      return;
    }
    const erroredFile = files.find((f) => f.meta.status !== 'queued');
    setFormDisabled(!!erroredFile);
    files.map((f) => {
      if (!f.meta.extra?.jobId) {
        const jobId = v4();
        setExtra(f.meta.id, { jobId: jobId });
      }
    });
  }, [files]);

  useEffect(() => {
    if (step !== BulkJobStep.UploadFiles) {
      return;
    }
    const notReadyToSubmitFile = files.find((f) => f.meta.status !== 'done');
    if (!notReadyToSubmitFile) {
      setStep(BulkJobStep.AnalyzingFiles);
    }
  }, [files]);

  useEffect(() => {
    if (step !== BulkJobStep.AnalyzingFiles) {
      return;
    }
    Promise.all(
      files.map(async (f) => {
        await call(
          JobsService.createJob({
            wsid: String(workspace?.id),
            requestBody: {
              id: f.meta.extra?.jobId,
              name: 'Unnamed',
              notes: '',
              status: 'JOB_STATUS_DRAFT',
            },
          }),
        );
        await call(
          MediaService.createMedia({
            requestBody: {
              id: f.meta.id,
              jobId: f.meta.extra?.jobId,
              fileName: f.meta.name,
              fileSize: f.file?.size?.toString() || '0',
              weight: 0,
            },
          }),
        );
        setExtra(f.meta.id, { analysisStatus: 'progress' });
        pollAnalysisResult(f.meta.id);
      }),
    );
  }, [step]);

  useEffect(() => {
    if (step !== BulkJobStep.AnalyzingFiles) {
      return;
    }
    if (files.find((f) => f.meta.status !== 'done')) {
      return;
    }
    const notReadyToSubmitFile = files.find((f) => f.meta.extra?.analysisStatus !== 'done');
    if (!notReadyToSubmitFile) {
      const totalDuration = files
        .map((a) => a.meta.extra?.duration || 0)
        .reduce((acc: number, curr: number) => acc + curr, 0);
      setTotalDuration(totalDuration);
      setIsLoading(false);
    }
  }, [files]);

  const pollAnalysisResult = (mediaId: string) => {
    let analysisChecksCount = 0;
    const interval = setInterval(async () => {
      try {
        const analysisResponse = await call(MediaService.getAnalysisStatus({ mediaId }));

        if (!analysisResponse) {
          clearInterval(interval);
          console.error('Cannot get analysisResponse');
          return;
        }

        if (analysisResponse?.returnvalue) {
          files.find((f) => {
            if (f.meta.id === mediaId) {
              const duration = analysisResponse?.returnvalue?.duration || 0;
              setExtra(f.meta.id, {
                duration,
                analysisStatus: duration ? 'done' : 'error',
              });
              return true;
            }
          });
          clearInterval(interval);
          return;
        }
        analysisChecksCount++;
        if (analysisChecksCount >= 60 || analysisResponse?.failedReason) {
          files.find((f) => {
            if (f.meta.id === mediaId) {
              const duration = analysisResponse?.returnvalue?.duration || 0;
              setExtra(f.meta.id, {
                duration,
                analysisStatus: 'error',
              });
              return true;
            }
          });
          clearInterval(interval);
          return;
        }
      } catch (e) {
        console.error('Cannot get analysis results', e);
      }
    }, 5000);
  };

  const createJobs = async (jobs: TCreateJob[]) => {
    if (!workspace?.id) {
      return;
    }

    setIsLoading(true);
    setJobs(jobs);
    try {
      const data = await call(() => JobsService.bulkUpdateJobs({ wsid: workspace?.id, requestBody: { jobs } }));

      if (!data.error) {
        setStep(BulkJobStep.SubmitPrice);
      } else {
        setError(data.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFiles = async () => {
    if (!workspace?.id) {
      return;
    }
    setIsLoading(true);
    setStep(BulkJobStep.UploadFiles);
    startAll();
  };

  const submitJobs = async () => {
    setIsLoading(true);
    setError(null);

    let isInsufficientBalance = false;
    try {
      await call(
        () =>
          PayService.payForBulkJobs({
            requestBody: {
              ids: jobs.map((job) => job.id),
            },
          }),
        {
          onError: (error: string) => {
            isInsufficientBalance = error.toLowerCase().includes('insufficient balance');
            setError(error);
            toast.error(error);
            if (isInsufficientBalance) {
              return;
            }
            setStep(BulkJobStep.JobsSubmittedWithErrors);
          },
          onSuccess: () => {
            setStep(BulkJobStep.JobsSubmittedSuccessfully);
          },
        },
      );
    } catch (error) {
      if (!isInsufficientBalance) {
        throw error;
      }
      // Server refused to charge, so maybe the balance is stale.
      await refreshBalance().catch(() => undefined);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalContext.Provider
      value={{
        step,
        setStep,
        isLoading,
        billingChecksInProgress,
        onCancel,
        onSuccess,
        error,
        layouts,
        createJobs,
        uploadFiles,
        jobs,
        totalDuration,
        submitJobs,
        isFormDisabled,
        balanceInCents,
        tierRate,
        refreshBalance,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useCurrentModalContext = () => useContext(ModalContext);
